/**
 * #272 — the ONE mixed-venue bulk cancel executor, plus the cross-surface
 * Polymarket in-flight fence.
 *
 * @ai-danger Every path that cancels more than one order at a time goes through
 * here. Before #272 there were two: `accountStore.cancelOrder` (fenced, #241)
 * and Position Tracker's inline `sweepCancelOrders`, which called `apiFetch`
 * directly and therefore bypassed the fence entirely — a sweep could re-DELETE
 * an ID a row click already owned. A second sweep implementation is the defect;
 * do not add one.
 *
 * Contract:
 *
 *  - The set is taken from a FROZEN snapshot. This function never reads a live
 *    order list, so orders that appear after the trader confirmed cannot join
 *    the batch.
 *  - Every Polymarket ID in the batch is CLAIMED synchronously, before the first
 *    relay request. Claiming lazily would leave a not-yet-reached ID clickable
 *    mid-sweep, and the row click would race the sweep to the same DELETE.
 *    (Kalshi's equivalent claim lives in `accountStore.cancelOrders`, which owns
 *    #241's state machine.)
 *  - Each venue's requests run sequentially; the two venues run concurrently.
 *    Their rate limits and refreshes are independent, so serializing across
 *    venues would only add latency.
 *  - Each affected venue refreshes EXACTLY ONCE, after its own leg — never once
 *    per order.
 *  - A 2xx is `submitted`, never `cancelled` or `succeeded`. The order is proven
 *    gone only when an authoritative snapshot stops listing it.
 *  - Failures stay per-order, with the venue's raw message, so a retry can be
 *    built from failures ALONE. Retrying a submitted ID resends a cancellation
 *    that already worked and fails misleadingly.
 */

import { cancelPolyOrderViaRelay } from '@/lib/polymarket/relay';
import type { FrozenCancelSnapshot, OrderCancelTarget } from './gameOrderSelection';
import type { KalshiCancelBatchResult } from './kalshiCancelLifecycle';

export type { KalshiCancelBatchResult } from './kalshiCancelLifecycle';

export interface CancelAttemptResult {
  target: OrderCancelTarget;
  /** `submitted` = the venue accepted the DELETE. NOT proof of removal. */
  status: 'submitted' | 'failed' | 'already-pending';
  error: string | null;
}

export interface BulkCancelReport {
  snapshot: FrozenCancelSnapshot;
  results: readonly CancelAttemptResult[];
  submitted: readonly OrderCancelTarget[];
  /** Retry material — and the ONLY retry material. */
  failed: readonly CancelAttemptResult[];
  /** Owned by another surface's in-flight cancel; neither sent nor retryable. */
  alreadyPending: readonly CancelAttemptResult[];
  /** Post-sweep refresh problems. Reported beside the per-order results rather
   *  than replacing them: a failed refresh says nothing about whether the
   *  cancels were accepted. */
  refreshErrors: readonly string[];
  /** The evidence breadcrumb is deliberately non-authoritative and may fail
   *  without blocking venue cancels. Keep that degradation visible. */
  auditError: string | null;
}

export const POLY_CANCEL_ALREADY_PENDING_MESSAGE =
  'Cancel already pending for this Polymarket order.';

export interface PolyCancelOnceResult {
  ok: boolean;
  error?: string;
  alreadyPending?: true;
}

// ── The Polymarket cancel lifecycle fence ───────────────────────────
//
// Module-scoped, and it deliberately outlives the DELETE response — this is
// #241's contract applied to the second venue (Brendan, PR #276 review).
//
// The first cut released the ID in a `finally`, i.e. the moment the request
// settled. Two ways that still permits a duplicate DELETE:
//
//   1. WITHIN a sweep. The venue loop is sequential, so releasing p-1 as soon as
//      it settles leaves p-1 clickable for the rest of the run while p-2..pN are
//      still going out.
//   2. AFTER a 2xx. A 2xx is an accepted request, not a removal. The hook's
//      optimistic row removal is LOCAL to one mount, and its post-cancel refresh
//      can legitimately return CLOB's pre-cancel book (venue propagation lag) —
//      that response is current-generation and current-identity, so every mount
//      re-renders the order. Unfenced, the next click re-DELETEs an order the
//      venue already accepted a cancel for, and it fails misleadingly *after*
//      the first cancel worked. That is precisely the #241 defect.
//
// So an accepted ID stays fenced until a CURRENT-IDENTITY authoritative
// open-orders snapshot no longer lists it. FOUR ways out (#748 added the
// fourth): authoritative removal, a failed request (nothing was accepted, so
// it is immediately retryable), an identity/reset boundary, and the bounded
// sync window elapsing.
//
// That fourth one is not a release. The entry SURVIVES as 'timed-out': it stops
// being pending — the trader gets the control back, because we can no longer
// confirm anything — while still carrying its own explanation, so the row does
// not quietly become indistinguishable from one that was never clicked.
type PolyCancelPhase =
  /** DELETE is in flight. */
  | 'requesting'
  /** DELETE returned 2xx; waiting for the ID to leave the authoritative list. */
  | 'awaiting-removal'
  /** #748 — the sync window elapsed with the ID still listed. Retryable, and
   *  NOT proof the order survived. Mirrors Kalshi's third phase (#241). */
  | 'timed-out';

interface PolyCancelEntry {
  phase: PolyCancelPhase;
  /** The account this claim belongs to; a snapshot for another account is not
   *  authority over it. */
  identity: string | null;
  timer: ReturnType<typeof setTimeout> | null;
  /** Set only on 'timed-out'. */
  message?: string;
}

/** #748 — what a row renders from. The timer and the owning identity are fence
 *  internals and deliberately absent: a consumer able to read them would be
 *  tempted to decide for itself whether a cancel may go out, and that decision
 *  belongs to this module alone. */
export interface PolyCancelState {
  phase: PolyCancelPhase;
  message?: string;
}

export type PolyCancelStates = Readonly<Partial<Record<string, PolyCancelState>>>;

const _polyCancels = new Map<string, PolyCancelEntry>();
let _polyFenceIdentity: string | null = null;

// ── #748: the fence, made observable ────────────────────────────────
//
// The lifecycle above has been correct since #272; what it lacked was any way
// to TELL anyone: it was a plain read of a module-scoped Map with no production
// caller, so a second mount rendering the same order had nothing to subscribe to — it
// kept drawing an ordinary cancel icon while the fence quietly refused the
// click, which is exactly the dead-looking button #748 reports. Kalshi never
// had this problem because #241 put its lifecycle in the account store, where
// every row already subscribes.
//
// Note the snapshot's STABLE IDENTITY. `useSyncExternalStore` re-renders
// whenever `getSnapshot` returns a new reference, so rebuilding the object on
// every call would loop forever; it is rebuilt only when the map mutates.
type PolyCancelListener = () => void;
const _polyCancelListeners = new Set<PolyCancelListener>();
let _polyCancelSnapshot: PolyCancelStates = Object.freeze({});

function buildPolyCancelSnapshot(): PolyCancelStates {
  const next: Record<string, PolyCancelState> = {};
  for (const [orderId, entry] of _polyCancels) {
    next[orderId] =
      entry.message === undefined
        ? { phase: entry.phase }
        : { phase: entry.phase, message: entry.message };
  }
  return Object.freeze(next);
}

/** Make the new state readable. ALWAYS synchronous: a reader between a mutation
 *  and its notification must never see the previous value — at the identity
 *  boundary that would be another account's cancel state. */
function rebuildPolyCancelSnapshot(): void {
  _polyCancelSnapshot = buildPolyCancelSnapshot();
}

/** Copy the set first: a listener may unsubscribe while being notified. */
function notifyPolyCancelListeners(): void {
  for (const listener of [..._polyCancelListeners]) listener();
}

/** Rebuild and notify. Must follow EVERY mutation of `_polyCancels` — a
 *  mutation nobody hears is the defect this section fixes. */
function emitPolyCancelChange(): void {
  rebuildPolyCancelSnapshot();
  notifyPolyCancelListeners();
}

/** Subscribe to fence changes; returns the unsubscribe. Prefer the
 *  `usePolyCancelStates` hook over calling this directly. */
export function subscribePolyCancels(listener: PolyCancelListener): () => void {
  _polyCancelListeners.add(listener);
  return () => {
    _polyCancelListeners.delete(listener);
  };
}

/** The current fence by order ID — same reference until something changes. */
export function getPolyCancelSnapshot(): PolyCancelStates {
  return _polyCancelSnapshot;
}

/** Bounded escape from a stalled synchronization, mirroring
 *  `KALSHI_CANCEL_SYNC_TIMEOUT_MS`. Without it a snapshot that never drops the
 *  ID (dead poll, venue that keeps listing it) would wedge the row forever.
 *  Releasing is NOT a claim that the order survived — only that we can no longer
 *  confirm, and the trader gets the control back. */
export const POLY_CANCEL_SYNC_TIMEOUT_MS = 5_000;

/** #748 — truthful copy for the timed-out row. Says "submitted", never
 *  "cancelled": a 2xx DELETE is a request the venue accepted, and the
 *  CONFIRMATION is what is missing. That one word is the deliberate divergence
 *  from `KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE`, whose Kalshi path can say
 *  "succeeded". Derived from the constant so the two cannot drift. */
export const POLY_CANCEL_SYNC_TIMEOUT_MESSAGE =
  `Cancel submitted, but Open Orders still shows this order after ${
    POLY_CANCEL_SYNC_TIMEOUT_MS / 1_000
  } seconds. Verify synchronization before retrying.`;

function clearPolyTimer(entry: PolyCancelEntry | undefined): void {
  if (entry?.timer) clearTimeout(entry.timer);
}

/** Drop one ID's fence without notifying — for callers releasing a batch, which
 *  emit once at the end rather than rebuilding the snapshot per ID. Returns
 *  whether anything actually changed. */
function releasePolyCancelSilent(orderId: string): boolean {
  const entry = _polyCancels.get(orderId);
  if (!entry) return false;
  clearPolyTimer(entry);
  _polyCancels.delete(orderId);
  return true;
}

/** Drop one ID's fence. Used for a failed request and on authoritative removal. */
function releasePolyCancel(orderId: string): void {
  if (releasePolyCancelSilent(orderId)) emitPolyCancelChange();
}

/**
 * #748 — the sync window elapsed with the ID still listed.
 *
 * This used to DELETE the entry, which handed back a control that looked
 * exactly like an ordinary, never-clicked Cancel button: the trader had no way
 * to tell "nothing happened yet" from "we asked and cannot confirm". The entry
 * now survives as a terminal, retryable state carrying its own explanation.
 *
 * Only an entry actually awaiting removal can time out — a stale timer must not
 * promote the fresh 'requesting' state of a later retry. Mirrors
 * `markKalshiCancelTimedOut`.
 */
function markPolyCancelTimedOut(orderId: string): void {
  const entry = _polyCancels.get(orderId);
  if (entry?.phase !== 'awaiting-removal') return;
  entry.phase = 'timed-out';
  entry.message = POLY_CANCEL_SYNC_TIMEOUT_MESSAGE;
  entry.timer = null;
  emitPolyCancelChange();
}

/**
 * True while a cancel for this order is in flight OR accepted-but-unconfirmed —
 * i.e. exactly while a further DELETE must be refused.
 *
 * ⚠️ #748: 'timed-out' is deliberately NOT pending, mirroring
 * `isKalshiCancelPending`. The trader gets the control back after the sync
 * window because we can no longer confirm anything — that is a statement about
 * OUR knowledge, not proof the order survived. Widening this to `.has(orderId)`
 * would wedge a timed-out row forever; narrowing it to 'requesting' would
 * reopen the duplicate-DELETE window #272 closed.
 */
export function isPolyCancelPending(orderId: string): boolean {
  return isPendingPolyCancelPhase(_polyCancels.get(orderId)?.phase);
}

/**
 * THE rule, in one place: which phases mean "a further DELETE must be refused".
 *
 * Split out so the row presentation can ask the same question of a snapshot
 * entry instead of re-deciding it from the phase itself. A second encoding is
 * how #725 earned a round-2 review — two mechanisms that agreed on the day and
 * drifted after.
 */
export function isPendingPolyCancelPhase(phase: PolyCancelPhase | undefined): boolean {
  return phase === 'requesting' || phase === 'awaiting-removal';
}

/**
 * Bind the fence to an account. Switching identity clears every entry: those
 * claims describe another account's orders, and a snapshot for the new account
 * must never be treated as authority over them (nor they over it).
 */
export function setPolyCancelIdentity(identity: string | null): void {
  if (identity === _polyFenceIdentity) return;
  const had = _polyCancels.size > 0;
  for (const entry of _polyCancels.values()) clearPolyTimer(entry);
  _polyCancels.clear();
  _polyFenceIdentity = identity;
  // ⚠️ This runs DURING RENDER — `usePolymarketRelayOrders` calls it in the
  // render body on purpose, so no fetch result can ever be reconciled against
  // the wrong account. Waking subscribers synchronously from here would
  // re-render one component from inside another's render. The map is already
  // correct for anyone who reads it; only the notification is deferred, by a
  // microtask, which still lands before paint and before any interaction.
  //
  // Guarded on `had` because an account switch with nothing fenced changes
  // nothing observable. (The identical-identity case never reaches here — the
  // early return above handles it, and MUST: this is a render-path call, so
  // clearing and notifying on every pass would loop.)
  if (had) {
    rebuildPolyCancelSnapshot();
    queueMicrotask(notifyPolyCancelListeners);
  }
}

/**
 * The authoritative step, mirroring `reconcileKalshiCancels`. Given the order-ID
 * set of a successfully parsed, current-identity open-orders snapshot, drop every
 * fenced ID the venue no longer lists — that disappearance is the only real
 * cancellation confirmation.
 *
 * Call this ONLY for a real fetch result. The hook's OPTIMISTIC local removal
 * must never reach here: it is this client's guess, not the venue's answer, and
 * treating it as authority would reopen the duplicate window immediately.
 */
export function reconcilePolyCancels(
  authoritativeOrderIds: Iterable<string>,
  identity: string | null,
): void {
  if (_polyCancels.size === 0) return;
  if (identity !== _polyFenceIdentity) return;
  const live = new Set<string>();
  for (const id of authoritativeOrderIds) {
    if (typeof id === 'string' && id.trim() !== '') live.add(id.trim());
  }
  let changed = false;
  for (const [orderId, entry] of [..._polyCancels]) {
    // An in-flight request is not yet a claim about the book — a snapshot taken
    // mid-DELETE must not retire it. A TIMED-OUT entry, though, is retired here
    // like an awaiting one (#748): the order finally leaving the authoritative
    // list is the confirmation the row was waiting for, and leaving the notice
    // up after that would keep explaining a synchronization that has since
    // resolved.
    if (entry.phase === 'requesting') continue;
    if (entry.identity !== identity) continue;
    if (!live.has(orderId)) changed = releasePolyCancelSilent(orderId) || changed;
  }
  if (changed) emitPolyCancelChange();
}

/** Test seam — the fence outlives a component, so a suite must be able to
 *  clear it between cases without reloading the module graph. */
export function _resetPolyCancelFenceForTests(): void {
  for (const entry of _polyCancels.values()) clearPolyTimer(entry);
  _polyCancels.clear();
  _polyFenceIdentity = null;
  // Notify unconditionally: a suite that resets between cases while a component
  // stays mounted must not leave that component rendering a dropped phase.
  emitPolyCancelChange();
}

/** Claim a whole Polymarket ID set atomically. Returns the IDs this caller now
 *  owns plus the ones another surface already owns. No awaits — the claim must
 *  complete inside one tick or the gap it leaves is the race it prevents. */
function claimPolyOrderIds(ids: readonly string[]): {
  owned: string[];
  alreadyPending: string[];
} {
  const owned: string[] = [];
  const alreadyPending: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    // Gated on isPolyCancelPending, NOT on `.has(id)`: a 'timed-out' entry is
    // the retry affordance (#748), so claiming over it is how a retry starts.
    // `.has(id)` here would refuse every retry forever. Mirrors
    // `beginKalshiCancel`, which likewise drops a previous timed-out state and
    // its message for the same ID.
    if (isPolyCancelPending(id)) alreadyPending.push(id);
    else {
      clearPolyTimer(_polyCancels.get(id));
      _polyCancels.set(id, {
        phase: 'requesting',
        identity: _polyFenceIdentity,
        timer: null,
      });
      owned.push(id);
    }
  }
  if (owned.length > 0) emitPolyCancelChange();
  return { owned, alreadyPending };
}

/** Release claims the venue loop never got to (it cannot throw today, but an
 *  orphaned `requesting` claim would wedge those rows). Deliberately leaves
 *  `awaiting-removal` entries alone — those are the accepted ones. */
function releaseUnsentPolyClaims(ids: Iterable<string>): void {
  let changed = false;
  for (const id of ids) {
    if (_polyCancels.get(id)?.phase === 'requesting') {
      changed = releasePolyCancelSilent(id) || changed;
    }
  }
  if (changed) emitPolyCancelChange();
}

/** Send the DELETE for an ID this caller ALREADY owns. Never claims — so it
 *  cannot be called on an unowned ID by accident, and the bulk path's up-front
 *  claim stays the single claim point.
 *
 *  On 2xx the fence is PROMOTED, not released: see the block comment above. */
async function sendOwnedPolyCancel(
  orderId: string,
  request: typeof cancelPolyOrderViaRelay,
): Promise<PolyCancelOnceResult> {
  try {
    const out = await request(orderId);
    if (out.ok) {
      const entry = _polyCancels.get(orderId);
      // Only promote an attempt that is still 'requesting': a reset or identity
      // switch during the request must not be undone by a late success.
      if (entry?.phase === 'requesting') {
        entry.phase = 'awaiting-removal';
        entry.timer = setTimeout(() => markPolyCancelTimedOut(orderId), POLY_CANCEL_SYNC_TIMEOUT_MS);
        // The row moves from "Canceling…" to "Refreshing status…" here, so the
        // promotion has to be announced like any other transition.
        emitPolyCancelChange();
      }
      return { ok: true };
    }
    // Nothing was accepted — immediately retryable.
    releasePolyCancel(orderId);
    return { ok: false, error: out.error };
  } catch (err) {
    releasePolyCancel(orderId);
    return { ok: false, error: err instanceof Error && err.message ? err.message : 'Cancel failed' };
  }
}

/**
 * Cancel ONE Polymarket order, at most once concurrently app-wide. The single-row
 * cancel path calls this so a row click and a bulk sweep cannot both submit the
 * same ID.
 */
export async function cancelPolyOrderOnce(
  orderId: string,
  request: typeof cancelPolyOrderViaRelay = cancelPolyOrderViaRelay,
): Promise<PolyCancelOnceResult> {
  const id = typeof orderId === 'string' ? orderId.trim() : '';
  if (!id) return { ok: false, error: 'Order has no ID — cannot cancel.' };
  const { owned } = claimPolyOrderIds([id]);
  if (owned.length === 0) {
    return { ok: false, error: POLY_CANCEL_ALREADY_PENDING_MESSAGE, alreadyPending: true };
  }
  return sendOwnedPolyCancel(id, request);
}

export interface BulkCancelDeps {
  /** The #241-fenced Kalshi batch — `accountStore.cancelOrders`. Pass
   *  `{ refreshAfter: false }` there; this executor owns the venue refresh. */
  cancelKalshiOrders: (ids: readonly string[]) => Promise<readonly KalshiCancelBatchResult[]>;
  requestPolymarketCancel: typeof cancelPolyOrderViaRelay;
  refreshKalshi: () => Promise<unknown>;
  refreshPolymarket: () => Promise<unknown>;
  /** Position Tracker only: durable evidence for the exact frozen set. */
  recordAttempt?: (snapshot: FrozenCancelSnapshot) => Promise<void>;
}

/** Dedupe by `${venue}:${orderId}` — a snapshot built from two overlapping
 *  rendered lists can legitimately repeat a row, and one order must produce one
 *  request. The same textual ID on two venues is NOT a duplicate. */
function dedupeTargets(targets: readonly OrderCancelTarget[]): OrderCancelTarget[] {
  const seen = new Set<string>();
  const out: OrderCancelTarget[] = [];
  for (const t of targets) {
    const key = t.key || `${t.venue}:${t.orderId}`;
    if (!t.orderId || seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

export async function executeBulkOrderCancel(
  snapshot: FrozenCancelSnapshot,
  deps: BulkCancelDeps,
): Promise<BulkCancelReport> {
  const targets = dedupeTargets(snapshot.targets);
  const kalshiTargets = targets.filter((t) => t.venue === 'kalshi');
  const polyTargets = targets.filter((t) => t.venue === 'polymarket');

  const byKey = new Map<string, CancelAttemptResult>();
  const refreshErrors: string[] = [];
  let auditError: string | null = null;

  // Claim the ENTIRE Polymarket set before any await. The audit request below
  // may yield; holding the claims closes the row-click race while it does.
  const polyClaim = claimPolyOrderIds(polyTargets.map((t) => t.orderId));
  const polyOwned = new Set(polyClaim.owned);

  if (deps.recordAttempt) {
    try {
      await deps.recordAttempt(snapshot);
    } catch (err) {
      auditError =
        err instanceof Error && err.message ? err.message : 'Cancel All attempt log unavailable';
    }
  }

  const kalshiLeg = async () => {
    if (kalshiTargets.length === 0) return;
    try {
      const batch = await deps.cancelKalshiOrders(kalshiTargets.map((t) => t.orderId));
      const byId = new Map(batch.map((row) => [row.orderId, row]));
      for (const target of kalshiTargets) {
        const row = byId.get(target.orderId);
        byKey.set(target.key, {
          target,
          // No row back for an ID we asked about means we cannot claim it was
          // submitted. Fail closed so it shows up as retryable.
          status: row?.status ?? 'failed',
          error: row?.error ?? (row ? null : 'Cancel not attempted'),
        });
      }
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Cancel failed';
      for (const target of kalshiTargets) {
        byKey.set(target.key, { target, status: 'failed', error: message });
      }
    } finally {
      try {
        await deps.refreshKalshi();
      } catch (err) {
        refreshErrors.push(
          `Kalshi: ${err instanceof Error && err.message ? err.message : 'refresh failed'}`,
        );
      }
    }
  };

  const polyLeg = async () => {
    if (polyTargets.length === 0) return;
    try {
      for (const target of polyTargets) {
        if (!polyOwned.has(target.orderId)) {
          byKey.set(target.key, {
            target,
            status: 'already-pending',
            error: POLY_CANCEL_ALREADY_PENDING_MESSAGE,
          });
          continue;
        }
        const out = await sendOwnedPolyCancel(target.orderId, deps.requestPolymarketCancel);
        byKey.set(target.key, {
          target,
          status: out.ok ? 'submitted' : 'failed',
          error: out.ok ? null : out.error ?? 'Cancel failed',
        });
      }
    } finally {
      // Only the claims the loop never sent. An ACCEPTED id stays fenced until
      // an authoritative snapshot drops it — releasing it here would reopen the
      // duplicate window the moment the sweep finished.
      releaseUnsentPolyClaims(polyOwned);
      try {
        await deps.refreshPolymarket();
      } catch (err) {
        refreshErrors.push(
          `Polymarket: ${err instanceof Error && err.message ? err.message : 'refresh failed'}`,
        );
      }
    }
  };

  await Promise.all([kalshiLeg(), polyLeg()]);

  const results = targets.map(
    (target) =>
      byKey.get(target.key) ?? {
        target,
        status: 'failed' as const,
        error: 'Cancel not attempted',
      },
  );

  return {
    snapshot,
    results,
    submitted: results.filter((r) => r.status === 'submitted').map((r) => r.target),
    failed: results.filter((r) => r.status === 'failed'),
    alreadyPending: results.filter((r) => r.status === 'already-pending'),
    refreshErrors,
    auditError,
  };
}

// ── Polymarket US cancel fence (F4) ─────────────────────────────────────
//
// The US venue is a THIRD id namespace: its order ids are venue-issued
// strings that could collide textually with a CLOB order hash, so they get
// their own map, snapshot and subscription rather than sharing the intl
// fence above. The lifecycle is the same #241/#272/#748 contract — claim
// synchronously, hold through 'awaiting-removal' until an authoritative
// open-orders snapshot stops listing the id, bounded by the sync timeout —
// with one difference: the DELETE carries the market slug (the venue
// requires it), so the claim records the slug for the request.
//
// Not yet in the mixed-venue bulk executor above (`executeBulkOrderCancel`):
// its target type is F3's `CancelVenue`, which this slice does not widen.

import {
  cancelPolyUsOrderViaRelay,
  type CancelPolyUsOrderResult,
} from '@/lib/polymarketUs/relay';

export type PolyUsCancelState = PolyCancelState;
export type PolyUsCancelStates = Readonly<Partial<Record<string, PolyUsCancelState>>>;

const _polyUsCancels = new Map<string, PolyCancelEntry>();
let _polyUsFenceIdentity: string | null = null;
const _polyUsListeners = new Set<PolyCancelListener>();
let _polyUsSnapshot: PolyUsCancelStates = Object.freeze({});

function rebuildPolyUsSnapshot(): void {
  const next: Record<string, PolyUsCancelState> = {};
  for (const [orderId, entry] of _polyUsCancels) {
    next[orderId] = entry.message === undefined ? { phase: entry.phase } : { phase: entry.phase, message: entry.message };
  }
  _polyUsSnapshot = Object.freeze(next);
}
function emitPolyUsChange(): void {
  rebuildPolyUsSnapshot();
  for (const listener of [..._polyUsListeners]) listener();
}

export function subscribePolyUsCancels(listener: PolyCancelListener): () => void {
  _polyUsListeners.add(listener);
  return () => {
    _polyUsListeners.delete(listener);
  };
}
export function getPolyUsCancelSnapshot(): PolyUsCancelStates {
  return _polyUsSnapshot;
}
export function isPolyUsCancelPending(orderId: string): boolean {
  return isPendingPolyCancelPhase(_polyUsCancels.get(orderId)?.phase);
}

function releasePolyUsSilent(orderId: string): boolean {
  const entry = _polyUsCancels.get(orderId);
  if (!entry) return false;
  clearPolyTimer(entry);
  _polyUsCancels.delete(orderId);
  return true;
}

/** Bind the US fence to an account (render-safe: notification is deferred). */
export function setPolyUsCancelIdentity(identity: string | null): void {
  if (identity === _polyUsFenceIdentity) return;
  const had = _polyUsCancels.size > 0;
  for (const entry of _polyUsCancels.values()) clearPolyTimer(entry);
  _polyUsCancels.clear();
  _polyUsFenceIdentity = identity;
  if (had) {
    rebuildPolyUsSnapshot();
    queueMicrotask(() => {
      for (const listener of [..._polyUsListeners]) listener();
    });
  }
}

/** Retire every awaiting/timed-out claim whose id an authoritative
 *  current-identity open-orders snapshot no longer lists. */
export function reconcilePolyUsCancels(authoritativeOrderIds: Iterable<string>, identity: string | null): void {
  if (_polyUsCancels.size === 0) return;
  if (identity !== _polyUsFenceIdentity) return;
  const live = new Set<string>();
  for (const id of authoritativeOrderIds) if (typeof id === 'string' && id.trim() !== '') live.add(id.trim());
  let changed = false;
  for (const [orderId, entry] of [..._polyUsCancels]) {
    if (entry.phase === 'requesting') continue;
    if (entry.identity !== identity) continue;
    if (!live.has(orderId)) changed = releasePolyUsSilent(orderId) || changed;
  }
  if (changed) emitPolyUsChange();
}

export function _resetPolyUsCancelFenceForTests(): void {
  for (const entry of _polyUsCancels.values()) clearPolyTimer(entry);
  _polyUsCancels.clear();
  _polyUsFenceIdentity = null;
  emitPolyUsChange();
}

/**
 * Cancel ONE Polymarket US order, at most once concurrently app-wide. The slug
 * is forwarded because the venue's cancel needs it (the relay can recover it
 * from the audit ledger, but a caller that knows it should say so).
 */
export async function cancelPolyUsOrderOnce(
  orderId: string,
  marketSlug?: string,
  request: (orderId: string, marketSlug?: string) => Promise<CancelPolyUsOrderResult> = cancelPolyUsOrderViaRelay,
): Promise<PolyCancelOnceResult> {
  const id = typeof orderId === 'string' ? orderId.trim() : '';
  if (!id) return { ok: false, error: 'Order has no ID — cannot cancel.' };
  if (isPolyUsCancelPending(id)) {
    return { ok: false, error: POLY_CANCEL_ALREADY_PENDING_MESSAGE, alreadyPending: true };
  }
  const prior = _polyUsCancels.get(id);
  if (prior) clearPolyTimer(prior);
  _polyUsCancels.set(id, { phase: 'requesting', identity: _polyUsFenceIdentity, timer: null });
  emitPolyUsChange();
  let out: CancelPolyUsOrderResult;
  try {
    out = await request(id, marketSlug);
  } catch (err) {
    releasePolyUsSilent(id);
    emitPolyUsChange();
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
  const entry = _polyUsCancels.get(id);
  if (!entry || entry.phase !== 'requesting') {
    // Identity moved mid-flight; the claim was cleared for us.
    return out.ok ? { ok: true } : { ok: false, error: out.error };
  }
  if (!out.ok) {
    releasePolyUsSilent(id);
    emitPolyUsChange();
    return { ok: false, error: out.error };
  }
  entry.phase = 'awaiting-removal';
  entry.timer = setTimeout(() => {
    const e = _polyUsCancels.get(id);
    if (!e || e.phase !== 'awaiting-removal') return;
    e.phase = 'timed-out';
    e.message = POLY_CANCEL_SYNC_TIMEOUT_MESSAGE;
    e.timer = null;
    emitPolyUsChange();
  }, POLY_CANCEL_SYNC_TIMEOUT_MS);
  emitPolyUsChange();
  return { ok: true };
}
